import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import FloatingAsset from '@/components/FloatingAsset';
import assetDiamond from '@/assets/asset-chrome-diamond.webp';

type AboutStat = {
  number: string;
  label: string;
};

type AboutValue = {
  title: string;
  description: string;
};

type AboutSectionProps = {
  tag?: string;
  title?: string;
  description?: string;
  stats?: AboutStat[];
  values?: AboutValue[];
};

const AboutSection = ({ tag, title, description, stats, values }: AboutSectionProps) => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true, margin: '-50px' });

  const fallbackValues = ['value1', 'value2', 'value3'].map((value) => ({
    title: t(`about.${value}.title`),
    description: t(`about.${value}.desc`),
  }));
  // No hardcoded fallback: metrics only appear once real ones are set in the CMS.
  const resolvedStats = stats ?? [];
  const resolvedValues = values?.length ? values : fallbackValues;

  return (
    <section ref={ref} className="section-y relative overflow-hidden" id="about">
      <FloatingAsset src={assetDiamond} size={110} className="top-32 right-20 opacity-35" floatDuration={10} />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="section-head-xl grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-body text-xs tracking-[0.2em] text-muted-foreground mb-3 uppercase">
              {tag || t('about.tag')}
            </p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-foreground">
              {title || t('about.title')}
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="self-end"
          >
            <p className="font-body text-foreground leading-relaxed text-base mb-4">
              {t('home.brandAnswer')}
            </p>
            <p className="font-body text-muted-foreground leading-relaxed text-base">
              {description || t('about.desc')}
            </p>
          </motion.div>
        </div>

        {resolvedStats.length > 0 && (
        <div ref={statsRef} className="section-head-xl grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {resolvedStats.map((stat, i) => (
            <motion.div
              key={`${stat.label}-${i}`}
              initial={{ opacity: 0, y: 30 }}
              animate={statsInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="text-center lg:text-left"
            >
              <motion.span
                className="font-display text-5xl lg:text-6xl font-bold text-foreground block"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={statsInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.2 + i * 0.1, type: 'spring', stiffness: 200 }}
              >
                {stat.number}
              </motion.span>
              <span className="font-body text-sm text-muted-foreground mt-2 block">
                {stat.label}
              </span>
            </motion.div>
          ))}
        </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {resolvedValues.map((value, i) => (
            <motion.div
              key={`${value.title}-${i}`}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.4 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -4, transition: { type: 'spring', stiffness: 300 } }}
              className="bg-secondary/30 rounded-2xl p-8 lg:p-10 hover:bg-secondary/50 transition-colors duration-500 cursor-default"
            >
              <motion.div
                className="w-2 h-2 bg-foreground rounded-full mb-8"
                whileHover={{ scale: 2 }}
                transition={{ type: 'spring', stiffness: 400 }}
              />
              <h3 className="font-display text-lg font-semibold text-foreground mb-3">
                {value.title}
              </h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                {value.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
